import { EChart, LegendIcon } from '/b2b/common/components/Charts';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { locale, translate } from '/b2b/common/helpers/i18n';

import DashboardContext from '../Dashboard.context';
import PropTypes from 'prop-types';
import WidgetContainer from '/b2b/common/components/Reporting/WidgetContainer';
import { escapeCSVValue } from '/b2b/common/helpers/CSV';
import { fetchUcReportsDashboard } from '/b2b/uc/services';
import { formatNumber } from '/b2b/common/helpers/Number';
import { makeDownloadBlob } from '/b2b/common/components/Reporting/helpers';
import merge from 'lodash/merge';
import { parseDate } from '/b2b/common/helpers/Date';
import useBoundCallback from '/b2b/common/helpers/Hooks/useBoundCallback';
import { useTheme } from '@mui/material/styles';
import { withAsyncData } from '/b2b/common/hoc/withAsyncData';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

export const REPORT_NAME = 'gpcExplicitConsents';

const commonSeries = {
    type: 'line',
    symbol: 'none',
    areaStyle: {
        opacity: 0.6,
    },
    lineStyle: {
        width: 0,
    },
};

/** @type {import('echarts').EChartsOption} */
const staticOptions = {
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'none',
        },
        formatter(/** @type {import('echarts/types/dist/shared').CallbackDataParams[]} */ params) {
            const { axisValueLabel } = params[0];
            const date = new Date(parseDate(axisValueLabel));
            const loc = locale();
            const formatOptions = {
                timeZone: 'UTC',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            };
            return `<b>${date.toLocaleString(loc, formatOptions)}</b><hr style="margin:0.25em 0"/>${params
                .map(({ dimensionNames, marker, seriesName, value }) => {
                    const count = value[dimensionNames[1]];
                    return `${marker} ${translate(seriesName)}: ${formatNumber(count, loc)}`;
                })
                .join('<br>')}`;
        },
    },
    legend: {
        icon: LegendIcon,
        orient: 'horizontal',
        top: 'top',
    },
    grid: {
        containLabel: true,
    },
    xAxis: {
        axisLine: { onZero: false, show: true },
        axisTick: { show: false },
        minorTick: { show: false },
        splitLine: { show: false },
        type: 'time',
        boundaryGap: false,
        axisLabel: {
            interval: 0,
            formatter: {
                hour: '',
                day: '{MMM}, {dd}',
                month: '{MMM}, {yyyy}',
                year: '{yyyy}',
            },
            showMaxLabel: true,
            showMinLabel: true,
        },
    },
    yAxis: {
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true },
        axisLabel: {
            align: 'right',
            margin: 0,
            showMaxLabel: false,
            showMinLabel: false,
            formatter: value => formatNumber(value, locale()),
        },
        nameRotate: 90,
        nameLocation: 'center',
        splitNumber: 2,
        type: 'value',
        nameTextStyle: {
            verticalAlign: 'middle',
        },
        min(value) {
            return Math.max(0, value.min - 10);
        },
    },
    series: [
        {
            ...commonSeries,
            name: 'uc.label.gpc',
            dimensions: [
                { name: 'date', type: 'time' },
                { name: 'gpc', type: 'int' },
            ],
        },
        {
            ...commonSeries,
            name: 'uc.label.explicit',
            dimensions: [
                { name: 'date', type: 'time' },
                { name: 'explicit', type: 'int' },
            ],
        },
    ],
};

const csvColumnMap = {
    date: 'common.date',
    gpc: 'uc.label.gpc',
    explicit: 'uc.label.explicit',
};

const processRow = (row, columnNames) => {
    return columnNames.reduce((acc, column) => {
        acc.push(row[column]);
        return acc;
    }, []);
};

function makeCSV(data) {
    const { gpcExplicitConsents } = data || {};
    const columnNames = Object.keys(csvColumnMap);
    // String CSV
    const csvFile = Object.values(gpcExplicitConsents)
        .reduce(
            (acc, row) => {
                const rowArray = processRow(row, columnNames);
                acc.push(rowArray.map(escapeCSVValue).join(','));
                return acc;
            },
            [
                columnNames
                    .map(column => translate(csvColumnMap[column]))
                    .map(escapeCSVValue)
                    .join(','),
            ]
        )
        .join('\n');
    return new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
}

const prepareData = data => {
    // This is where we would prepare the data if we needed to
    return data;
};

const GpcConsents = props => {
    const { data: providedData, filters, loading: providedLoading, shouldRetranslate } = props;
    const { data, error, load, loading } = useContext(DashboardContext);
    const { gpcExplicitConsents = [] } = data;
    const noResults = gpcExplicitConsents.length === 0;
    const isLoading = loading || providedLoading;

    const theme = useTheme();
    /** @type {import('echarts').EChartsOption} */
    const memoOption = useMemo(() => {
        const newOption = merge({}, staticOptions, {
            legend: {
                left: theme.spacing(1.5),
            },
            grid: {
                left: parseInt(theme.spacing(4.5), 10),
                right: parseInt(theme.spacing(4.5), 10),
            },
            yAxis: {
                show: gpcExplicitConsents.length > 0,
                name: translate('common.visitors'),
                nameGap: parseInt(theme.spacing(3.5), 10),
            },
            dataset: {
                source: [...gpcExplicitConsents],
            },
            series: staticOptions.series.map(series => {
                return {
                    ...series,
                    name: translate(series.name),
                };
            }),
        });
        if (gpcExplicitConsents.length === 1) {
            // Since we can't show a line with only 1 entry, unshift an entry showing 0 values
            const date = new Date(gpcExplicitConsents[0].date);
            date.setDate(date.getDate() - 1);
            newOption.dataset.source.unshift({ date, explicit: 0, gpc: 0 });
            newOption.xAxis.axisLabel.align = 'right';
        }
        return newOption;
    }, [gpcExplicitConsents, shouldRetranslate]);

    /** @type {() => Promise<UcReportsData>} */
    const fetchData = useBoundCallback(
        filters =>
            fetchUcReportsDashboard({
                filters: { ...filters, requestedCharts: [REPORT_NAME] },
            }).then(prepareData),
        [filters]
    );

    const echartsInstance = useRef(null);
    const helpers = useMemo(
        () => ({
            makeCSV,
            name: 'gpc-vs-explicit-consent',
        }),
        []
    );
    const makeDownload = useBoundCallback(makeDownloadBlob, [echartsInstance, data, helpers]);
    const onChartReady = useCallback(chart => {
        echartsInstance.current = chart;
    }, []);

    useLayoutEffect(() => {
        providedData ? load(Promise.resolve(prepareData(providedData))) : load(fetchData());
    }, [providedData, filters]);

    return (
        <WidgetContainer
            disabled={noResults}
            downloadFormats={['csv', 'png']}
            echartsInstance={echartsInstance}
            error={error}
            title="uc.section.dashboards.title.gpcVsExplicit"
            tooltip="uc.section.dashboards.tooltip.gpcVsExplicit"
        >
            <EChart
                onChartReady={onChartReady}
                option={memoOption}
                loading={isLoading}
                makeDownload={makeDownload}
                noResults={noResults}
            />
        </WidgetContainer>
    );
};

GpcConsents.propTypes = {
    data: PropTypes.object,
    filters: PropTypes.object,
    loading: PropTypes.bool,
    shouldRetranslate: PropTypes.string.isRequired,
};

export default withAsyncData(withRetranslate(GpcConsents), DashboardContext);
